<template>
  <v-layout>
    <v-flex>
      <!-- Search etc -->
      <ProjectSearch v-on:updateProjectFilter="onFilterUpdate" :filterProject="getProjectFilter"/>

      <v-container fluid>
        <v-row class="justify-center">
          <v-col cols="12" lg="9">
            <!-- PROJECTS -->
            <v-expansion-panels multiple v-model="panel">
              <ProjectPanel
                v-for="project in getProjects"
                :project="project"
                :duplicatedScenarios="getDuplicatedScenarios"
                :countries="getCountriesMap"
                :key="project.uuid"
                :ref="'panels'"
                @newScenario="newScenario"
                @deleteProject="askDeleteProject"
              />

              <ConfirmationDialog
                :popUpValue="itemToDelete"
                :title="deleteItemDialogTitle"
                @confirm="confirmDeleteItem"
                @cancel="cancelDeleteItem"
              >
                <template v-slot:message>
                  <span>{{deleteItemDialogMessage}}</span>
                </template>
              </ConfirmationDialog>
              <DuplicationDialog
                v-if="duplicateDialog"
                @cancel="duplicateDialog = false"
                @confirm="duplicateScenarioToProject"
                :popUpValue="itemToDuplicate"
                :itemsProp="getProjects"
              />
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </v-flex>
  </v-layout>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import ProjectSearch from '@/components/Projects/ProjectSearch.vue';
import ProjectPanel from '@/components/Projects/ProjectPanel.vue';
import { ScenarioMeta } from '@/classes/ScenarioMeta';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { EventBus, EventBusEvents } from '@/helpers/event-bus';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import DuplicationDialog from '@/components/DuplicationDialog.vue';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { CountryMeta } from '@/classes/CountryMeta';
import { Filter } from '@/interfaces/PayloadsType';

enum ItemTypeRef {
  project = 'project',
  scenario = 'scenario'
}

@Component({
  components: {
    ProjectSearch,
    ProjectPanel,
    DuplicationDialog,
    ConfirmationDialog
  }
})
export default class ProjectList extends Vue {
  private readonly stepPath: string = 'project';

  private projectIdToDelete: string | null = null;
  private scenarioIdToDelete: string | null = null;
  private panel: number[] = [];

  private itemToDeleteConf: { id: string; type: ItemTypeRef } | null = null;

  private itemToDuplicate: {
    scenario: ScenarioMeta;
    projectId: string;
  } | null = null;
  private duplicateDialog: boolean = false;
  private deleteItemDialogRoot: string | null = null;

  // COMPUTED =======================================================
  get getProjects(): ProjectMeta[] {
    return this.$store.getters['structure/getProjects'];
  }
  get getCurrentProject() {
    return this.$store.getters['project/getCurrentProject'];
  }
  get getPanelState() {
    return this.$store.getters['project/getPanelState'];
  }
  get getProjectFilter(): Filter {
    return this.$store.getters['structure/getProjectFilter'];
  }
  get getCountriesMap(): Map<string, CountryMeta> {
    return this.$store.getters['structure/getCountriesMap'];
  }
  get getScenariosList(): ScenarioMeta[] {
    return this.$store.getters['structure/getScenariosList'];
  }
  get getReloadTrigger(): any {
    return this.$store.getters['scenario/getReloadTrigger'];
  }
  get getDuplicatedScenarios(): Map<string, ScenarioExt> {
    return this.$store.getters['scenario/getDuplicatedScenarios'];
  }

  get itemToDelete(): ScenarioMeta | ProjectMeta | null {
    if (this.itemToDeleteConf === null) {
      return null;
    }
    if (this.itemToDeleteConf.type === ItemTypeRef.scenario) {
      return (
        this.getScenariosList.find(
          sc => sc.uuid === this.itemToDeleteConf!.id
        ) || null
      );
    }
    if (this.itemToDeleteConf.type === ItemTypeRef.project) {
      return (
        this.getProjects.find(p => p.uuid === this.itemToDeleteConf!.id) || null
      );
    }
    return null;
  }
  get itemToDeleteName(): string {
    return (this.itemToDelete && this.itemToDelete.name) || '';
  }

  get deleteItemDialogTitle() {
    return this.deleteItemDialogRoot
      ? `${this.$t(this.deleteItemDialogRoot + '_title')}`
      : '';
  }

  get deleteItemDialogMessage() {
    return this.deleteItemDialogRoot
      ? `${this.$t(this.deleteItemDialogRoot + '_message')} ${
          this.itemToDeleteName
        }. ${this.$t('project.confirmation')}`
      : '';
  }

  // METHODS =======================================================
  private newScenario(projectId: string) {
    this.$store
      .dispatch('stepper/resetVisitedSteps')
      .then(() => {
        return this.$store.dispatch('scenario/createScenario', projectId);
      })
      .then(() => {
        this.$router.push('/scenario_step_wizard');
      });
  }
  private loadScenario(scenarioId: string) {
    this.$store
      .dispatch('stepper/resetVisitedSteps')
      .then(() => {
        return this.$store.dispatch('scenario/loadScenario', scenarioId);
      })
      .then(() => {
        this.$router.push('/scenario_step_wizard');
      });
  }
  private duplicateScenario(payload: {
    scenario: ScenarioMeta;
    projectId: string;
  }) {
    this.itemToDuplicate = payload;
    this.duplicateDialog = true;
  }

  private duplicateScenarioToProject(payload: {
    scenario: ScenarioMeta;
    project: { name: string; uuid: string };
  }) {
    this.duplicateDialog = false;
    this.$store.dispatch('scenario/duplicateScenario', {
      scenarioId: payload.scenario.uuid,
      projectId: payload.project.uuid
    });
  }

  private askDeleteProject(projectId: string) {
    this.itemToDeleteConf = { id: projectId, type: ItemTypeRef.project };
  }
  private askDeleteScenario(senarioId: string) {
    this.itemToDeleteConf = { id: senarioId, type: ItemTypeRef.scenario };
  }
  private cancelDeleteItem() {
    this.itemToDeleteConf = null;
  }
  private confirmDeleteItem() {
    if (this.itemToDelete) {
      if (this.itemToDelete instanceof ProjectMeta) {
        this.$store.dispatch('structure/deleteProject', this.itemToDelete.uuid);
      } else if (this.itemToDelete instanceof ScenarioMeta) {
        this.$store.dispatch('scenario/deleteScenario', this.itemToDelete.uuid);
      }
    }
    this.itemToDeleteConf = null;
  }

  private getDeleteItemDialogType(): string {
    if (!this.itemToDelete) {
      return `${this.stepPath}.no_item_to_delete`;
    }
    return `${this.stepPath}.confirm_delete_${this.itemToDeleteConf!.type}`;
  }

  private onFilterUpdate(filter: any) {
    this.$store.dispatch('structure/saveFilter', filter).then(() => {
      this.$store.dispatch('structure/loadProjects');
    });
  }

  /**
   * A specific scrollTo function
   * @refIndex is the project index
   * the refs inside 'panels' are set in the same order as in getProjects
   */
  private scrollToProject(refIndex: number | null) {
    if (refIndex === null) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const vm: any = this;
    const element = vm.$refs.panels[refIndex];
    const top = element.$el.offsetTop;
    window.scrollTo(0, top);
  }

  private saveCurrentProject(projectId: string) {
    this.$store.dispatch('project/saveCurrentProject', projectId);
  }

  // HOOKS =======================================================
  private created() {
    /**
     * We need to be sure that we get our opened panels before mounted hook
     */
    this.panel = this.getPanelState;
  }

  private mounted() {
    EventBus.$on(EventBusEvents.loadScenario, this.loadScenario);
    EventBus.$on(EventBusEvents.saveLatestOpenedProject, this.saveCurrentProject);
    EventBus.$on(EventBusEvents.deleteScenario, this.askDeleteScenario);
    EventBus.$on(EventBusEvents.duplicateScenario, this.duplicateScenario);
    /**
     * If we have visited a project, then we find its index
     */
    if (this.getCurrentProject && this.getCurrentProject.uuid) {
      const indexOfCurrentProject = this.getProjects.findIndex(p => p.uuid === this.getCurrentProject.uuid);
      this.scrollToProject(indexOfCurrentProject);
    }
  }

  @Watch('getReloadTrigger', { immediate: true })
  private onReloadTrigger() {
    this.$store.dispatch('structure/loadProjects');
  }

  @Watch('itemToDeleteConf', { immediate: true })
  private onItemDeleteConfChange(val: any) {
    if (this.itemToDeleteConf) {
      this.deleteItemDialogRoot = this.getDeleteItemDialogType();
    }
  }
  @Watch('panel')
  private onPanelToggle(val: any) {
    this.$store.dispatch('project/savePanelState', this.panel);
  }

  private beforeDestroy() {
    EventBus.$off(EventBusEvents.loadScenario, this.loadScenario);
    EventBus.$off(EventBusEvents.duplicateScenario, this.saveCurrentProject);
    EventBus.$off(EventBusEvents.deleteScenario, this.askDeleteScenario);
    EventBus.$off(EventBusEvents.saveLatestOpenedProject, this.saveCurrentProject);
  }
}
</script>

<style lang="stylus">
#app {
  .delete-project-text {
    text-align: center;
    padding: 15px;
    margin-bottom: 0px;
    color: #FFFFFF;
  }

  .delete-title {
    font-size: 14px;
  }

  .delete-item-title {
    margin-right: 8px;
  }
}

.v-card--reveal {
  align-items: center;
  bottom: 0;
  justify-content: center;
  opacity: 0.5;
  position: absolute;
  width: 100%;
}

.hoverable {
  transform: translateY(0px);
  transition: 0.5s;

  &:hover {
    box-shadow: 0px 0px 20px -3px #555555;
    transform: translateY(-5px);
    transition: 0.5s ease-out;
  }
}

.dialog-title {
  font-size: 14px;
}

.dialog-action-btn {
  font-size: 16px;
}

.first-letter-capital:first-letter {
  text-transform: capitalize;
}

.my-dialog {
  position: absolute;
  top: 2em;
}

.hint {
  color: #71717188;
  font-size: 14px;
  font-style: italic;
}
</style>
